/**
 * DistrictLeaderboardPage — `/admin/organisations/:orgId/leaderboard`
 *
 * Ranks every district under a Province, Country Office or the Global HQ by
 * giving, growth (new members vs the window before), average attendance and
 * membership — so provinces can celebrate progress rather than guess at it.
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Trophy, Coins, UserCheck, Users, TrendingUp, TrendingDown, Minus, MapPin,
  Share2, Download, Copy,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'last_90_days', label: 'Last 90 days' },
  { key: 'this_year', label: 'This year' },
  { key: 'all_time', label: 'All time' },
];

const money = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const MEDALS = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-800', 'bg-orange-300 text-orange-950'];

const GrowthChip = ({ pct, testId }) => {
  const up = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span data-testid={testId}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
            flat ? 'bg-slate-100 text-slate-500'
              : up ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
      <Icon className="h-3 w-3" />{up ? '+' : ''}{pct}%
    </span>
  );
};

/** Creates (or reuses) a public share link, then offers the PNG and WhatsApp. */
const ShareButton = ({ orgId, period, currency, disabled }) => {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/organisations/${orgId}/leaderboard/share`,
        { period, reporting_currency: currency });
      setShare(data);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
    setBusy(false);
  };

  const publicUrl = share ? `${window.location.origin}${share.path}` : '';
  const imageUrl = share ? `${API_URL}${share.image_path}` : '';

  return (
    <>
      <Button size="sm" variant="outline" className="h-8" onClick={open} disabled={disabled || busy}
              data-testid="leaderboard-share-btn">
        <Share2 className="mr-1.5 h-3.5 w-3.5" /> Share
      </Button>
      <Dialog open={!!share} onOpenChange={(o) => !o && setShare(null)}>
        <DialogContent data-testid="leaderboard-share-dialog">
          <DialogHeader>
            <DialogTitle>Share this leaderboard</DialogTitle>
            <DialogDescription>
              Anyone with the link sees the live rankings — no login needed. The image is sized for
              WhatsApp and Instagram.
            </DialogDescription>
          </DialogHeader>
          {share && (
            <div className="space-y-3">
              <img src={imageUrl} alt="Leaderboard card"
                   className="mx-auto w-full max-w-[260px] rounded-lg border border-slate-200"
                   data-testid="leaderboard-share-preview" />
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-2">
                <input readOnly value={publicUrl} data-testid="leaderboard-share-link"
                       className="min-w-0 flex-1 bg-transparent text-xs text-slate-600 outline-none" />
                <Button size="sm" variant="ghost" data-testid="leaderboard-copy-link"
                        onClick={() => {
                          navigator.clipboard?.writeText(publicUrl);
                          toast.success('Link copied');
                        }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" data-testid="leaderboard-download-png">
                  <a href={imageUrl} download={`leaderboard-${share.token}.png`}>
                    <Download className="mr-1.5 h-3.5 w-3.5" /> Download PNG
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid="leaderboard-whatsapp">
                  <a target="_blank" rel="noreferrer"
                     href={`https://wa.me/?text=${encodeURIComponent(`District leaderboard\n${publicUrl}`)}`}>
                    <Share2 className="mr-1.5 h-3.5 w-3.5" /> Send on WhatsApp
                  </a>
                </Button>
                <Button asChild size="sm" variant="ghost" data-testid="leaderboard-open-public">
                  <a href={publicUrl} target="_blank" rel="noreferrer">Open public page</a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const DistrictLeaderboardPage = () => {
  const { orgId } = useParams();
  const [period, setPeriod] = useState('last_30_days');
  const [currency, setCurrency] = useState('USD');

  const { data: fx } = useQuery({
    queryKey: ['org-fx-rates'],
    queryFn: async () => (await api.get('/admin/organisations/fx/rates')).data,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['district-leaderboard', orgId, period, currency],
    queryFn: async () => (await api.get(
      `/admin/organisations/${orgId}/district-leaderboard`,
      { params: { period, reporting_currency: currency } })).data,
    retry: false,
  });

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="district-leaderboard-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/churches" className="text-slate-400 hover:text-slate-700"
                  data-testid="leaderboard-back">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <Trophy className="h-5 w-5 text-amber-500" /> District leaderboard
              </h1>
              <p className="text-xs text-slate-500" data-testid="leaderboard-scope">
                {error ? 'Not available for this organisation'
                  : data?.scope ? `${data.scope.type_label} · ${data.scope.name}` : 'Loading scope…'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ShareButton orgId={orgId} period={period} currency={currency} disabled={!!error} />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                    data-testid="leaderboard-currency-select"
                    className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
              {Object.keys(fx?.rates || { USD: 1 }).sort().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="flex overflow-hidden rounded-md border border-slate-200 bg-white">
              {PERIODS.map((p) => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                        data-testid={`leaderboard-period-${p.key}`}
                        className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                          period === p.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        {error ? (
          <Card data-testid="leaderboard-error">
            <CardContent className="py-14 text-center">
              <MapPin className="mx-auto mb-2 h-7 w-7 text-slate-400" />
              <p className="text-sm text-slate-600">{getErrorMessage(error)}</p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <Card><CardContent className="space-y-3 py-6">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[['Districts', totals.districts], ['Assemblies', totals.assemblies],
                ['Members', totals.members],
                [`Giving (${data?.reporting_currency})`, money(totals.giving)]].map(([label, value]) => (
                <Card key={label} data-testid={`leaderboard-total-${label.split(' ')[0].toLowerCase()}`}>
                  <CardContent className="pb-3 pt-4">
                    <p className="text-xl font-bold tabular-nums text-slate-900">{value ?? 0}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {rows.length === 0 ? (
              <Card data-testid="leaderboard-empty">
                <CardContent className="py-16 text-center">
                  <Trophy className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                  <p className="text-sm text-slate-600">No districts sit under {data?.scope?.name} yet.</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Add a district to this branch and its assemblies will start rolling up here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="divide-y divide-slate-100">
                  {rows.map((d, i) => (
                    <div key={d.id} className="flex flex-wrap items-center gap-4 px-4 py-3"
                         data-testid={`leaderboard-row-${d.id}`}>
                      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold ${
                        MEDALS[i] || 'bg-slate-100 text-slate-500'}`}
                            data-testid={`leaderboard-rank-${d.id}`}>
                        {d.rank}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-900">{d.name}</p>
                        <p className="text-[11px] text-slate-400">
                          {d.parent_name ? `under ${d.parent_name} · ` : ''}
                          {d.assemblies} assembl{d.assemblies === 1 ? 'y' : 'ies'}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                        <span className="flex items-center gap-1 tabular-nums text-slate-700"
                              title="Giving in the reporting currency"
                              data-testid={`leaderboard-giving-${d.id}`}>
                          <Coins className="h-3.5 w-3.5 text-slate-400" />{money(d.giving)}
                        </span>
                        <span className="flex items-center gap-1 tabular-nums text-slate-700"
                              title="Average attendance per meeting"
                              data-testid={`leaderboard-attendance-${d.id}`}>
                          <UserCheck className="h-3.5 w-3.5 text-slate-400" />{d.attendance_avg}
                        </span>
                        <span className="flex items-center gap-1 tabular-nums text-slate-700"
                              data-testid={`leaderboard-members-${d.id}`}>
                          <Users className="h-3.5 w-3.5 text-slate-400" />{d.members}
                        </span>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]"
                                 data-testid={`leaderboard-new-${d.id}`}>
                            +{d.new_members} joined
                          </Badge>
                          <GrowthChip pct={d.growth_pct} testId={`leaderboard-growth-${d.id}`} />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            <p className="text-center text-[11px] text-slate-400">
              Ranked by giving, then by new members. Growth compares this window
              with the one immediately before it.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default DistrictLeaderboardPage;
